/**
 * @file date-picker/date-range-picker.tsx
 *
 * DateRangePicker — calendar UI for date range selection.
 * Equivalent to `DateRangePicker` composable in Android Compose Material3.
 *
 * Differences from DatePicker:
 * - Highlights range between start and end (SecondaryContainer)
 * - Headline displays "Start date → End date"
 * - Default title: "Select dates" (plural)
 *
 * @see https://m3.material.io/components/date-pickers/overview
 */

import { AnimatePresence, m } from "motion/react";
import * as React from "react";
import { cn } from "../../../lib/utils";
import { PICKER_SELECTOR_ANIM } from "../shared/animation-tokens";
import {
	formatDate,
	getNavigableMonth,
	isMonthNavigable,
} from "../shared/calendar-model";
import type { DateRangePickerProps } from "./date-picker.types";
import { CalendarGrid } from "./subcomponents/calendar-grid";
import {
	CalendarHeader,
	type CalendarSelector,
} from "./subcomponents/calendar-header";
import { MonthPickerList } from "./subcomponents/month-picker-list";
import { YearPickerList } from "./subcomponents/year-picker-list";

/**
 * DateRangePicker calendar UI.
 *
 * @example
 * ```tsx
 * const state = useDateRangePickerState();
 *
 * <DateRangePicker
 *   state={state}
 *   title="Select travel dates"
 * />
 * ```
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
	state,
	showModeToggle = false,
	title,
	className,
}) => {
	const {
		selectedStartMs,
		selectedEndMs,
		displayedMonthMs,
		displayMode,
		yearRange,
		locale,
		selectableDates,
		selectDate,
		navigateMonth,
		navigateToMonth,
		setDisplayMode,
	} = state;

	const [activeSelector, setActiveSelector] =
		React.useState<CalendarSelector>(null);
	const [slideDirection, setSlideDirection] = React.useState<
		"prev" | "next" | null
	>(null);

	const handlePrev = React.useCallback(() => {
		setSlideDirection("prev");
		navigateMonth("prev");
	}, [navigateMonth]);

	const handleNext = React.useCallback(() => {
		setSlideDirection("next");
		navigateMonth("next");
	}, [navigateMonth]);

	const handleYearSelect = React.useCallback(
		(year: number) => {
			const d = new Date(displayedMonthMs);
			navigateToMonth(year, d.getUTCMonth());
			setActiveSelector(null);
		},
		[displayedMonthMs, navigateToMonth],
	);

	const handleMonthSelect = React.useCallback(
		(month: number) => {
			const d = new Date(displayedMonthMs);
			navigateToMonth(d.getUTCFullYear(), month);
			setActiveSelector(null);
		},
		[displayedMonthMs, navigateToMonth],
	);

	// ── Headline: "Start → End" ──────────────────────────────────────────────────
	const formatShort = React.useCallback(
		(ms: number | null): string => {
			if (ms === null) return "—";
			return formatDate(ms, locale, {
				month: "short",
				day: "numeric",
				timeZone: "UTC",
			});
		},
		[locale],
	);

	const headline = `${formatShort(selectedStartMs)} – ${formatShort(selectedEndMs)}`;

	const currentYear = new Date(displayedMonthMs).getUTCFullYear();
	const currentMonth = new Date(displayedMonthMs).getUTCMonth();

	const computedAriaLabel =
		typeof title === "string" ? title : "Date range picker";
	const canNavigatePrev =
		getNavigableMonth(displayedMonthMs, "prev", yearRange, selectableDates) !==
		null;
	const canNavigateNext =
		getNavigableMonth(displayedMonthMs, "next", yearRange, selectableDates) !==
		null;
	const canNavigatePreviousYear = isMonthNavigable(
		currentYear - 1,
		yearRange,
		selectableDates,
	);
	const canNavigateNextYear = isMonthNavigable(
		currentYear + 1,
		yearRange,
		selectableDates,
	);

	return (
		<fieldset
			className={cn("flex flex-col select-none", className)}
			style={{ width: "min(100%, 360px)" }}
			aria-label={computedAriaLabel}
		>
			<legend className="sr-only">{computedAriaLabel}</legend>
			{/* Header */}
			<CalendarHeader
				displayedMonthMs={displayedMonthMs}
				activeSelector={activeSelector}
				displayMode={displayMode}
				showModeToggle={showModeToggle}
				title={title ?? "Select dates"}
				headline={headline}
				locale={locale}
				canNavigatePrev={canNavigatePrev}
				canNavigateNext={canNavigateNext}
				canNavigatePreviousYear={canNavigatePreviousYear}
				canNavigateNextYear={canNavigateNextYear}
				onPrev={handlePrev}
				onNext={handleNext}
				onPreviousYear={() => navigateToMonth(currentYear - 1, currentMonth)}
				onNextYear={() => navigateToMonth(currentYear + 1, currentMonth)}
				onToggleSelector={(selector) =>
					setActiveSelector((active) => (active === selector ? null : selector))
				}
				onToggleMode={() => {
					setDisplayMode(displayMode === "picker" ? "input" : "picker");
					setActiveSelector(null);
				}}
			/>

			{/* Body */}
			<div
				className="relative flex-1 overflow-hidden"
				style={{ minHeight: "296px" }}
			>
				<AnimatePresence mode="wait">
					{activeSelector === "month" ? (
						<m.div
							id="date-picker-selection"
							key="month-list"
							initial={PICKER_SELECTOR_ANIM.initial}
							animate={PICKER_SELECTOR_ANIM.animate}
							exit={PICKER_SELECTOR_ANIM.exit}
							className="absolute inset-0"
						>
							<MonthPickerList
								selectedMonth={currentMonth}
								locale={locale}
								onMonthSelect={handleMonthSelect}
							/>
						</m.div>
					) : activeSelector === "year" ? (
						<m.div
							id="date-picker-selection"
							key="year-list"
							initial={PICKER_SELECTOR_ANIM.initial}
							animate={PICKER_SELECTOR_ANIM.animate}
							exit={PICKER_SELECTOR_ANIM.exit}
							className="absolute inset-0"
						>
							<YearPickerList
								selectedYear={currentYear}
								yearRange={yearRange}
								selectableDates={selectableDates}
								onYearSelect={handleYearSelect}
							/>
						</m.div>
					) : (
						<CalendarGrid
							key="calendar"
							displayedMonthMs={displayedMonthMs}
							slideDirection={slideDirection}
							locale={locale}
							selectableDates={selectableDates}
							selectedMs={null}
							selectedStartMs={selectedStartMs}
							selectedEndMs={selectedEndMs}
							isRangePicker
							onDaySelect={selectDate}
						/>
					)}
				</AnimatePresence>
			</div>
		</fieldset>
	);
};

DateRangePicker.displayName = "DateRangePicker";
